/*
 * Acabamentos Bracci: carrossel de esferas com pontos por página e filtro por categoria.
 * Lógica portada do <script> inline da featured-collection.liquid do tema antigo,
 * agora como Web Component, para funcionar com várias instâncias e com o editor de temas.
 */
(() => {
  if (customElements.get('bracci-acabamentos')) return;

  const ALL_VALUE = '__all__';

  // Tokens que marcam "incluir todos os acabamentos nesta categoria".
  const WILDCARDS = new Set(['', 'all', 'todos', 'todas']);

  // Normaliza para comparação: minúsculas, sem acentos, espaços colapsados.
  function normalize(text) {
    return (text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  class BracciAcabamentos extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-ba-list]');
      this.dotsWrap = this.querySelector('[data-ba-dots]');
      this.filterWrap = this.querySelector('[data-ba-filter]');
      if (!this.list || !this.dotsWrap) return;

      this.allSlides = Array.from(this.list.querySelectorAll('.ba-item'));
      this.slides = this.allSlides.slice();
      this.anchors = [];
      this.anchorOffsets = [];
      this.dots = [];
      this.currentDot = 0;

      this.querySelector('[data-ba-prev]')?.addEventListener('click', () => {
        if (this.currentDot > 0) this.goToDot(this.currentDot - 1);
      });
      this.querySelector('[data-ba-next]')?.addEventListener('click', () => {
        if (this.currentDot < this.anchors.length - 1) this.goToDot(this.currentDot + 1);
      });

      this.list.addEventListener('scroll', this.onScroll, { passive: true });

      this.onResize = () => {
        this.rebuild(false);
        if (!this.anchors.length) return;
        this.goToDot(Math.min(this.currentDot, this.anchors.length - 1));
      };
      window.addEventListener('resize', this.onResize);

      // Se o tamanho do conteúdo mudar (imagens), recalcula os pontos de parada.
      this.resizeObserver = new ResizeObserver(() => {
        this.computeAnchorOffsets();
        this.alignArrows();
      });
      this.resizeObserver.observe(this.list);

      // Como no original: monta depois do load, quando as larguras já estão certas.
      const start = () => setTimeout(() => this.afterLayout(), 0);
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('click', this.onDocumentClick);
      this.resizeObserver?.disconnect();
    }

    afterLayout() {
      const filtered = this.setupCategoryFilter();
      if (!filtered) this.rebuild(true);
    }

    /* ---------- Pontos e rolagem ---------- */

    refreshVisibleSlides() {
      this.slides = this.allSlides.filter((slide) => !slide.classList.contains('is-filtered-out'));
    }

    getBreakpoint() {
      const width = window.innerWidth;
      if (width < 768) return 'mobile'; // até 11 pontos
      if (width < 1024) return 'tablet'; // 6 pontos
      return 'desktop'; // um ponto por posição inicial possível
    }

    computeItemsPerView() {
      const { slides, list } = this;
      if (slides.length <= 1) return 1;
      // Distância entre dois itens, incluindo o espaço entre eles.
      let delta = slides[1].offsetLeft - slides[0].offsetLeft;
      if (delta <= 0) delta = slides[0].getBoundingClientRect().width;
      if (delta <= 0) return 1;
      return Math.max(1, Math.round(list.clientWidth / delta));
    }

    buildAnchors() {
      const breakpoint = this.getBreakpoint();
      const itemsPerView = this.computeItemsPerView();
      const maxStart = Math.max(0, this.slides.length - itemsPerView);

      // Distribui as posições 0..maxStart em `count` pontos.
      const spaced = (count) => {
        if (maxStart === 0 || count <= 1) return [0];
        const out = [];
        let last = -1;
        for (let k = 0; k < count; k++) {
          let index = Math.round((k / (count - 1)) * maxStart);
          if (index === last) index = Math.min(maxStart, index + 1);
          if (k === 0) index = 0;
          if (k === count - 1) index = maxStart;
          if (out[out.length - 1] !== index) out.push(index);
          last = index;
        }
        out[0] = 0;
        out[out.length - 1] = maxStart;
        return out;
      };

      if (breakpoint === 'desktop') {
        this.anchors = Array.from({ length: maxStart + 1 }, (_, i) => i);
      } else if (breakpoint === 'tablet') {
        this.anchors = spaced(6);
      } else {
        this.anchors = spaced(Math.min(11, this.slides.length));
      }
    }

    buildDots() {
      const buttons = this.anchors
        .map(
          (_, i) =>
            `<button type="button" class="ba-dot" aria-label="Ir para a página ${i + 1}"><span></span></button>`
        )
        .join('');
      this.dotsWrap.innerHTML = `<div class="ba-dots__counter"><div class="ba-dots__wrapper">${buttons}</div></div>`;
      this.dots = Array.from(this.dotsWrap.querySelectorAll('.ba-dot'));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goToDot(i)));
    }

    computeAnchorOffsets() {
      const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      this.anchorOffsets = this.anchors.map((i) => {
        const left = this.slides[i]?.offsetLeft || 0;
        return Math.max(0, Math.min(left, maxScroll));
      });
    }

    setActiveDot(index) {
      this.dots.forEach((dot) => dot.classList.remove('is-active'));
      this.dots[index]?.classList.add('is-active');
    }

    goToDot(index) {
      this.currentDot = Math.max(0, Math.min(index, this.anchors.length - 1));
      const left = this.anchorOffsets[this.currentDot] ?? 0;
      this.list.scrollTo({ left, behavior: 'smooth' });
      this.setActiveDot(this.currentDot);
    }

    // Mantém o ponto ativo em sincronia com a rolagem manual.
    onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const x = this.list.scrollLeft;
        let best = 0;
        let bestDistance = Infinity;
        this.anchorOffsets.forEach((offset, i) => {
          const distance = Math.abs(offset - x);
          if (distance < bestDistance) {
            best = i;
            bestDistance = distance;
          }
        });
        if (best !== this.currentDot) {
          this.currentDot = best;
          this.setActiveDot(best);
        }
        this.ticking = false;
      });
    };

    // Centraliza as setas na altura das esferas (e não no meio do carrossel todo, que
    // inclui nomes e pontos). Mede a imagem de verdade, então acompanha o tamanho
    // escolhido no editor. Usa a escala de repouso: a medida é feita fora do hover.
    alignArrows() {
      const slider = this.querySelector('.ba-slider');
      const image = this.slides[0]?.querySelector('.ba-card__image');
      if (!slider || !image) return;
      const sliderTop = slider.getBoundingClientRect().top;
      const box = image.getBoundingClientRect();
      if (!box.height) return;
      this.style.setProperty('--ba-arrow-top', `${Math.round(box.top - sliderTop + box.height / 2)}px`);
    }

    rebuild(goStart = true) {
      this.refreshVisibleSlides();
      this.buildAnchors();
      this.buildDots();
      this.computeAnchorOffsets();
      this.alignArrows();
      if (goStart) {
        this.currentDot = 0;
        this.goToDot(0);
      } else {
        this.setActiveDot(this.currentDot);
      }
    }

    /* ---------- Links dos acabamentos ---------- */

    // Com categoria escolhida, cada esfera leva para a coleção da categoria filtrada
    // pelo acabamento. Sem categoria, volta para o link próprio do acabamento.
    updateFinishLinks(category) {
      this.list.querySelectorAll('.ba-card').forEach((link) => {
        const item = link.closest('.ba-item');
        const finishValue = item.dataset.filterValue || item.dataset.title;

        if (category && category.url) {
          const separator = category.url.includes('?') ? '&' : '?';
          const encodedValue = encodeURIComponent(finishValue).replace(/%2B/g, '+');
          link.href = `${category.url}${separator}filter.p.m.custom.nome_do_acabamento=${encodedValue}`;
        } else {
          link.href = item.dataset.originalUrl || '#';
        }
      });
    }

    /* ---------- Filtro por categoria ---------- */

    setupCategoryFilter() {
      const { filterWrap } = this;
      if (!filterWrap) return false;

      const trigger = filterWrap.querySelector('.ba-filter__trigger');
      const menu = filterWrap.querySelector('.ba-filter__menu');
      const currentEl = filterWrap.querySelector('.ba-filter__current');
      if (!trigger || !menu || !currentEl) return false;

      const allLabel = this.dataset.allLabel || 'Ver todas';
      const showSelect = this.dataset.showSelect === 'true';
      const showAllOption = this.dataset.showAllOption === 'true';

      // Cada categoria: { key, label, url, finishesSet, isWildcard }
      const categories = [];
      const dataEl = this.querySelector('[data-ba-categories]');
      if (dataEl) {
        try {
          JSON.parse(dataEl.textContent).forEach((category) => {
            if (!category) return;
            const label = (category.name || '').trim();
            const key = normalize(label);
            if (!key) return;
            const lines = (category.finishes || '').split(/\r?\n/).map(normalize);
            const meaningful = lines.filter(Boolean);
            // Coringa: campo vazio ou alguma linha com um dos tokens reservados.
            const isWildcard = meaningful.length === 0 || lines.some((line) => WILDCARDS.has(line));
            categories.push({
              key,
              label,
              url: category.url || '',
              isWildcard,
              finishesSet: isWildcard ? null : new Set(meaningful),
            });
          });
        } catch (error) {
          // JSON inválido: segue sem filtro.
        }
      }

      const applyFilter = (value, label) => {
        currentEl.textContent = label;
        menu.querySelectorAll('.ba-filter__option').forEach((option) => {
          option.setAttribute('aria-selected', option.dataset.value === value ? 'true' : 'false');
        });

        const category = value === ALL_VALUE ? null : categories.find((c) => c.key === value);
        this.allSlides.forEach((slide) => {
          let visible = value === ALL_VALUE;
          if (!visible && category) {
            if (category.isWildcard) {
              visible = true;
            } else {
              // Aceita o ID estável (data-handle) ou o nome (data-title).
              const handle = normalize(slide.dataset.handle);
              const title = normalize(slide.dataset.title);
              visible =
                (handle && category.finishesSet.has(handle)) || (title && category.finishesSet.has(title));
            }
          }
          slide.classList.toggle('is-filtered-out', !visible);
        });

        // Como no tema antigo: troca o endereço da página pelo da categoria.
        // Pode ser desligado no editor ("Trocar o endereço da página ao filtrar").
        if (category && category.url && this.dataset.updateUrl !== 'false') {
          window.history.replaceState({}, '', category.url);
        }

        this.updateFinishLinks(category);
        this.rebuild(true);
      };

      if (categories.length === 0) {
        filterWrap.style.display = 'none';
        return false;
      }

      if (!showSelect) {
        filterWrap.style.display = 'none';
        applyFilter(ALL_VALUE, allLabel);
        return true;
      }

      // Opções do select.
      menu.innerHTML = '';
      const makeOption = (value, label) => {
        const li = document.createElement('li');
        li.className = 'ba-filter__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.value = value;
        li.tabIndex = -1;
        li.textContent = label;
        return li;
      };
      if (showAllOption) menu.appendChild(makeOption(ALL_VALUE, allLabel));
      categories.forEach((category) => menu.appendChild(makeOption(category.key, category.label)));

      filterWrap.classList.add('is-active');

      // Começa na categoria escolhida no editor; sem ela, em "Ver todas" ou, sem essa
      // opção, na primeira categoria.
      const initial = categories.find((category) => category.key === normalize(this.dataset.initialCategory));
      if (initial) {
        applyFilter(initial.key, initial.label);
      } else if (showAllOption) {
        applyFilter(ALL_VALUE, allLabel);
      } else {
        applyFilter(categories[0].key, categories[0].label);
      }

      const openMenu = () => {
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      };
      const closeMenu = () => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };
      const isOpen = () => !menu.hidden;

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (isOpen()) closeMenu();
        else openMenu();
      });

      menu.addEventListener('click', (event) => {
        const option = event.target.closest('.ba-filter__option');
        if (!option) return;
        applyFilter(option.dataset.value, option.textContent);
        closeMenu();
        trigger.focus();
      });

      // Fecha ao clicar fora.
      this.onDocumentClick = (event) => {
        if (isOpen() && !filterWrap.contains(event.target)) closeMenu();
      };
      document.addEventListener('click', this.onDocumentClick);

      // Teclado no botão: abre com seta para baixo, Enter ou Espaço.
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          if (!isOpen()) {
            event.preventDefault();
            openMenu();
            const first =
              menu.querySelector('.ba-filter__option[aria-selected="true"]') ||
              menu.querySelector('.ba-filter__option');
            first?.focus();
          }
        } else if (event.key === 'Escape' && isOpen()) {
          closeMenu();
        }
      });

      // Teclado no menu: navega, seleciona e fecha.
      menu.addEventListener('keydown', (event) => {
        const options = Array.from(menu.querySelectorAll('.ba-filter__option'));
        const focused = document.activeElement.closest('.ba-filter__option');
        let index = options.indexOf(focused);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          index = (index + 1) % options.length;
          options[index].focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          index = (index - 1 + options.length) % options.length;
          options[index].focus();
        } else if (event.key === 'Home') {
          event.preventDefault();
          options[0].focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          options[options.length - 1].focus();
        } else if (event.key === 'Enter' || event.key === ' ') {
          if (focused) {
            event.preventDefault();
            applyFilter(focused.dataset.value, focused.textContent);
            closeMenu();
            trigger.focus();
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();
          trigger.focus();
        } else if (event.key === 'Tab') {
          closeMenu();
        }
      });

      return true;
    }
  }

  customElements.define('bracci-acabamentos', BracciAcabamentos);
})();
